import { useState, useEffect, useMemo } from 'react';
import { Grid, Typography } from '@mui/material';
import { Section } from '../../../styles';

import { Chart } from 'react-google-charts';

import LoadingComponent from '@components/templates/LoadingComponent';
import ConsultantCard from '@/pages/Common/EnterpriseDetails/EnterprisePage/components/ConsultantCard';
import ConsultantDateFilter from '@/pages/Common/EnterpriseDetails/EnterprisePage/components/ConsultantDateFilter';
import useDashboardCoursesService from '@/services/dashboard-courses';
import CoursesUserPerfomance from '@/pages/Common/EnterpriseDetails/EnterprisePage/tabs/Courses/components/CoursesUserPerfomance';

import { TitleGrid, NoData, PieChartGrid, PaperContainer, ContainerYears, Div } from './style';
import { useParams } from 'react-router-dom';

import DataProgress from './components/DataProgress';

const Courses = () => {
    const [loading, setLoading] = useState(false);
    const [years, setYears] = useState(2023);
    const [filterYear, setFilterYear] = useState(new Date().getFullYear());
    const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);

    const [data, setData] = useState({});
    const [graph, setGraph] = useState({});

    const { getDashboardCourses, getDashboardCoursesBarGraph } = useDashboardCoursesService();

    const { id } = useParams();
    const enterpriseId = id;

    const optionsPieChart = {
        pieHole: 0.5,
        legend: {
            position: 'right',
            maxLines: 1,
            alignment: 'center',
            textStyle: {
                fontSize: 14
            }
        },
        pieSliceText: 'none',
        colors: ['#0F4B44', '#339E92', '#84DBD1'],
        chartArea: {
            width: '100%',
            left: 0
        }
    };

    const optionsColumnChart = {
        legend: {
            position: 'bottom',
            alignment: 'center',
            textStyle: {
                fontSize: 14
            }
        },
        colors: ['#057165', '#7BC2B9'],
        chartArea: {
            width: '95%'
        },

        fontSize: 12
    };

    const handleData = async () => {
        setLoading(true);

        getDashboardCourses(filterYear, filterMonth == 'Todos' ? null : filterMonth, enterpriseId)
            .then((resp) => {
                setData(resp.data);
            })
            .catch((error) => {});

        getDashboardCoursesBarGraph(filterYear, enterpriseId)
            .then((resp) => {
                setGraph(resp.data);
            })
            .catch((error) => {})
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        handleData();
    }, []);

    const pieChartMonth = useMemo(() => {
        const rows = data.pieChartMonth ? data.pieChartMonth.map((item) => [item.title, item.started]) : [];
        return [['Curso', 'Iniciados'], ...rows];
    }, [data.pieChartMonth]);

    const pieChartYear = useMemo(() => {
        const rows = data.pieChartYear ? data.pieChartYear.map((item) => [item.title, item.started]) : [];
        return [['Curso', 'Iniciados'], ...rows];
    }, [data.pieChartYear]);

    const chartData = useMemo(() => {
        if (!graph.metrics) return [['Mês', 'Consultores que iniciaram um curso', 'Consultores que finalizaram um curso']];

        return [
            ['Mês', 'Consultores que iniciaram um curso', 'Consultores que finalizaram um curso'],
            ...Object.entries(graph.metrics).map(([month, values]) => [month, values.started ?? 0, values.completed ?? 0])
        ];
    }, [graph.metrics]);

    return (
        <Section>
            {loading ? (
                <LoadingComponent hideLogo />
            ) : (
                <>
                    <Grid container>
                        <ConsultantDateFilter
                            years={years}
                            filterYear={filterYear}
                            setFilterYear={setFilterYear}
                            filterMonth={filterMonth}
                            setFilterMonth={setFilterMonth}
                            handleFilterClick={() => {
                                handleData();
                            }}
                            handleClean={() => {
                                setFilterYear('');
                                setFilterMonth('');
                            }}
                        />
                    </Grid>
                    <Grid container>
                        <Grid container xs={6} spacing={3} mr={3}>
                            <Grid item xs={12}>
                                <ConsultantCard title="Cursos iniciados" value={data.started_courses} year />
                            </Grid>
                            <Grid item xs={12}>
                                <ConsultantCard title="Cursos concluídos" value={data.completed_courses} year />
                            </Grid>
                        </Grid>
                        <PieChartGrid container xs={6}>
                            {!data.pieChartYear || pieChartYear.length <= 1 ? (
                                <NoData>Nenhum curso foi visto no ano filtrado.</NoData>
                            ) : (
                                <>
                                    <TitleGrid item xs={12}>
                                        <Typography variant="h5" fontWeight="bold">
                                            3 cursos mais assistidos
                                        </Typography>
                                        <Typography variant="h6">(Por ano)</Typography>
                                    </TitleGrid>
                                    <Grid item xs={12} height="300px" width="100%">
                                        <Chart chartType="PieChart" height="100%" data={pieChartYear} options={optionsPieChart} />
                                    </Grid>
                                </>
                            )}
                        </PieChartGrid>
                    </Grid>
                    {graph.metrics && (
                        <Grid container mt={3}>
                            <PaperContainer height="500px">
                                <ContainerYears>
                                    <Div width="100%">
                                        <h1>Consultores / Cursos</h1>
                                        <p>
                                            <b>{filterYear} |</b> {graph.totalCourses} Cursos
                                        </p>
                                    </Div>
                                </ContainerYears>
                                <Chart chartType="ColumnChart" height="100%" width="100%" data={chartData} options={optionsColumnChart} />
                            </PaperContainer>
                        </Grid>
                    )}
                    <Grid container marginTop="2rem" height="300px" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Grid item width="32%">
                            <ConsultantCard
                                title="Tempo médio em videoaulas"
                                description="(Tempo em que o consultor passa assistindo às aulas no mês)"
                                value={data.average_time}
                                color="#7BC2B9"
                                margin="60px"
                            />
                        </Grid>
                        <Grid item width="32%">
                            <ConsultantCard
                                title="Total de cursos concluídos"
                                description="(De acordo com a % de consultores que finalizaram um curso no mês)"
                                value={<DataProgress value={data.completed_courses_month} maxValue={data.consultants} />}
                            />
                        </Grid>
                        <PieChartGrid container width="32%">
                            {!data.pieChartMonth || pieChartMonth.length <= 1 ? (
                                <NoData>Nenhum curso foi visto no mês filtrado.</NoData>
                            ) : (
                                <>
                                    <TitleGrid item xs={12}>
                                        <Typography variant="h5" fontWeight="bold">
                                            3 cursos mais assistidos
                                        </Typography>
                                        <Typography variant="h6">(Por mês)</Typography>
                                    </TitleGrid>
                                    <Grid item xs={12} height="220px" width="100%">
                                        <Chart chartType="PieChart" height="100%" data={pieChartMonth} options={optionsPieChart} />
                                    </Grid>
                                </>
                            )}
                        </PieChartGrid>
                    </Grid>

                    <Grid container marginTop="2rem">
                        <CoursesUserPerfomance
                            enterpriseId={enterpriseId}
                            filterYear={filterYear}
                            filterMonth={filterMonth == 'Todos' ? null : filterMonth}
                        />
                    </Grid>
                </>
            )}
        </Section>
    );
};

export default Courses;
